"use client";
import { useEffect, useRef, useState } from "react";

export type ScrollAnimatedSplitHeadingSectionData = {
  staticText: string;
  animatedText: string;
  animatedGradient?: string;
  subtitle?: string;
};

const BG = [
  "linear-gradient(180deg, rgba(220,214,252,0.75) 0%, rgba(232,228,253,0.5) 35%, rgba(255,255,255,0.1) 70%, #FFFFFF 100%)",
  "radial-gradient(ellipse 65% 65% at 0% 0%, rgba(167,139,250,0.32) 0%, rgba(167,139,250,0.06) 60%, transparent 85%)",
  "radial-gradient(ellipse 65% 65% at 100% 0%, rgba(139,130,255,0.28) 0%, rgba(139,130,255,0.05) 60%, transparent 85%)",
].join(", ");

const ScrollAnimatedSplitHeadingSection = ({
  staticText,
  animatedText,
  animatedGradient = "linear-gradient(to right, #5B50F5, #8B82FF, #BDB8FF)",
}: ScrollAnimatedSplitHeadingSectionData) => {
  const sectionRef = useRef<HTMLDivElement>(null);
  const [revealPct, setRevealPct] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = sectionRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      // 0 when section enters from bottom, 1 when section top reaches viewport top
      const progress = 1 - rect.top / window.innerHeight;
      setRevealPct(Math.round(Math.min(100, Math.max(0, progress * 100))));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const fontSize = "clamp(60px, 15vw, 220px)";

  const text = (dimColor: string, gradient: string | null) => (
    <span className="font-inter" style={{ lineHeight: 1.05, display: "block" }}>
      <span style={{ fontSize, fontWeight: 600, letterSpacing: "-0.04em", color: dimColor }}>
        {staticText}
      </span>
      <span
        style={{
          fontSize,
          fontWeight: 700,
          letterSpacing: "-0.04em",
          marginLeft: "0.1em",
          ...(gradient
            ? {
                backgroundImage: gradient,
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                color: "transparent",
                WebkitTextFillColor: "transparent",
              }
            : { color: dimColor }),
        }}
      >
        {animatedText}
      </span>
    </span>
  );

  return (
    <section ref={sectionRef} className="w-full" style={{ background: BG }}>
      <div
        className="w-full overflow-hidden flex items-center justify-center"
        style={{ paddingTop: "10vh", paddingBottom: "2vh", background: BG }}
      >
        <div className="relative w-full px-8 md:px-16 text-center">
          {/* Dim base */}
          <div>{text("rgba(139,130,255,0.18)", null)}</div>

          {/* Bright revealed layer — scroll-driven, fills and unfills with scroll */}
          <div
            className="absolute inset-0 flex items-center justify-center px-8 md:px-16"
            style={{ clipPath: `inset(0 ${100 - revealPct}% 0 0)` }}
          >
            {text("#9CA3AF", animatedGradient)}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ScrollAnimatedSplitHeadingSection;
